import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [userId, setUserId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [latestArrival, setLatestArrival] = useState(null);
  // 알림 변경 신호기 — 각 화면이 이 값 변화를 보고 자체 재조회
  const [notifSignal, setNotifSignal] = useState(0);
  const [queueTick, setQueueTick] = useState(0);
  const currentUserIdRef = useRef(null);

  // moowoo_gift_expired 실시간 합산 버퍼 및 중복 방지 Ref
  const moowooExpiredBufferRef = useRef([]);
  const moowooExpiredTimerRef = useRef(null);
  const triggeredNotifIdsRef = useRef(new Set());
  const initialExpiredTriggeredRef = useRef(false);
  const initialRejectedTriggeredRef = useRef(false);
  const rejectedQueueRef = useRef([]);

  const clearLatestArrival = useCallback(() => {
    setLatestArrival(null);
  }, []);

  // 거절 알림 큐 자동 소비 트리거 (latestArrival 이 비워지거나 새 항목이 큐에 진입할 때 1건 인출)
  useEffect(() => {
    if (!latestArrival && rejectedQueueRef.current.length > 0) {
      const nextItem = rejectedQueueRef.current.shift();
      if (nextItem) {
        setLatestArrival(nextItem);
      }
    }
  }, [latestArrival, queueTick]);

  // 1. 사용자 ID 획득 및 인증 상태 변화 감지
  useEffect(() => {
    let isMounted = true;

    // 초기 세션 확인
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      const initialUserId = session?.user?.id || null;
      if (currentUserIdRef.current !== initialUserId) {
        currentUserIdRef.current = initialUserId;
        setUserId(initialUserId);
      }
    });

    // 인증 상태 변화 리스너 (동일 ID인 경우 상태 갱신 방지)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      const newUserId = session?.user?.id || null;
      if (currentUserIdRef.current !== newUserId) {
        currentUserIdRef.current = newUserId;
        setUserId(newUserId);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // 2. Realtime 구독 개설 및 초기 안 읽은 알림 조회
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLatestArrival(null);
      moowooExpiredBufferRef.current = [];
      if (moowooExpiredTimerRef.current) {
        clearTimeout(moowooExpiredTimerRef.current);
        moowooExpiredTimerRef.current = null;
      }
      triggeredNotifIdsRef.current.clear();
      initialExpiredTriggeredRef.current = false;
      initialRejectedTriggeredRef.current = false;
      rejectedQueueRef.current = [];
      return;
    }

    let isMounted = true;

    // ★(1) 먼저 Realtime 구독을 개설한다 (조회와 구독 사이 알림 유실 방지)
    console.log('[관제탑] 알림 구독 시작');
    const channel = supabase
      .channel(`global-notifications-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[관제탑] 알림 수신:', payload);
          const newNotif = payload.new;
          if (!newNotif || newNotif.is_read === true) return;

          // ★Realtime INSERT: group_invite_received 알림은 즉시 latestArrival에 전달 (기존 동작 100% 유지)
          if (newNotif.type === 'group_invite_received') {
            setLatestArrival(newNotif);
          }

          // ★Realtime INSERT: moowoo_gift_rejected 알림은 큐에 추가하고 tick 신호 발생 (상한 없음, remainingCount 없음)
          if (newNotif.type === 'moowoo_gift_rejected') {
            if (!triggeredNotifIdsRef.current.has(newNotif.id)) {
              triggeredNotifIdsRef.current.add(newNotif.id);
              rejectedQueueRef.current.push(newNotif);
              setQueueTick((v) => v + 1);
            }
          }

          // ★Realtime INSERT: moowoo_gift_expired 알림은 1000ms 디바운스로 합산하여 전달
          if (newNotif.type === 'moowoo_gift_expired') {
            moowooExpiredBufferRef.current.push(newNotif);
            if (moowooExpiredTimerRef.current) {
              clearTimeout(moowooExpiredTimerRef.current);
            }
            moowooExpiredTimerRef.current = setTimeout(() => {
              const buffer = [...moowooExpiredBufferRef.current];
              moowooExpiredBufferRef.current = [];
              if (buffer.length === 0) return;

              const untriggered = buffer.filter((n) => !triggeredNotifIdsRef.current.has(n.id));
              if (untriggered.length === 0) return;

              untriggered.forEach((n) => triggeredNotifIdsRef.current.add(n.id));
              const totalAmount = untriggered.reduce(
                (sum, n) => sum + (Number(n.payload?.amount) || 0),
                0
              );

              setLatestArrival({
                id: 'aggregated-moowoo-gift-expired',
                aggregated: true,
                type: 'moowoo_gift_expired',
                totalAmount,
                ids: untriggered.map((n) => n.id),
                payload: {},
              });
            }, 1000);
          }

          setNotifications((prev) => {
            // 중복 방지 (고유 id 검사)
            if (prev.some((n) => n.id === newNotif.id)) return prev;
            return [newNotif, ...prev];
          });
          setNotifSignal((prev) => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[관제탑] 알림 수신:', payload);
          const updatedNotif = payload.new;
          if (!updatedNotif) return;

          setNotifications((prev) => {
            // ★UPDATE로 is_read가 true로 바뀌면 관제탑 목록에서 제거
            if (updatedNotif.is_read === true) {
              return prev.filter((n) => n.id !== updatedNotif.id);
            }
            // is_read가 여전히 false면 해당 항목 내용 교체 (없으면 추가)
            const exists = prev.some((n) => n.id === updatedNotif.id);
            if (exists) {
              return prev.map((n) => (n.id === updatedNotif.id ? updatedNotif : n));
            }
            return [updatedNotif, ...prev];
          });
          setNotifSignal((prev) => prev + 1);
        }
      )
      .subscribe();

    // ★(2) 그 다음 안 읽은 알림을 초기 조회한다
    const fetchInitialNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('recipient_id', userId)
          .eq('is_read', false)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) {
          console.error('Error fetching initial notifications:', error);
          return;
        }

        if (isMounted && data) {
          console.log(`[관제탑] 안 읽은 알림 ${data.length}건`);

          // 초기 로드 경로 만료 알림 합산 배너 (최초 1회 트리거)
          if (!initialExpiredTriggeredRef.current) {
            initialExpiredTriggeredRef.current = true;
            const initialExpired = data.filter(
              (n) => n.type === 'moowoo_gift_expired' && !triggeredNotifIdsRef.current.has(n.id)
            );
            if (initialExpired.length > 0) {
              initialExpired.forEach((n) => triggeredNotifIdsRef.current.add(n.id));
              const totalAmount = initialExpired.reduce(
                (sum, n) => sum + (Number(n.payload?.amount) || 0),
                0
              );
              setLatestArrival({
                id: 'aggregated-moowoo-gift-expired',
                aggregated: true,
                type: 'moowoo_gift_expired',
                totalAmount,
                ids: initialExpired.map((n) => n.id),
                payload: {},
              });
            }
          }

          // 초기 로드 경로 거절 알림 순차 큐 (오래된 순 최대 3건, 최초 1회 트리거)
          if (!initialRejectedTriggeredRef.current) {
            initialRejectedTriggeredRef.current = true;
            const allRejected = data
              .filter((n) => n.type === 'moowoo_gift_rejected' && !triggeredNotifIdsRef.current.has(n.id))
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

            if (allRejected.length > 0) {
              const queueItems = allRejected.slice(0, 3);
              const remainingCount = allRejected.length - queueItems.length;

              queueItems.forEach((n) => triggeredNotifIdsRef.current.add(n.id));

              if (remainingCount > 0 && queueItems.length > 0) {
                const lastIndex = queueItems.length - 1;
                queueItems[lastIndex] = {
                  ...queueItems[lastIndex],
                  remainingCount,
                };
              }

              rejectedQueueRef.current.push(...queueItems);
              setQueueTick((v) => v + 1);
            }
          }

          setNotifications((prev) => {
            // 실시간 수신된 항목과 초기 조회 데이터를 고유 id 기준으로 병합 (중복 방지)
            const existingIds = new Set(prev.map((n) => n.id));
            const newItems = data.filter((n) => !existingIds.has(n.id));
            return [...prev, ...newItems];
          });
        }
      } catch (err) {
        console.error('Error in fetchInitialNotifications:', err);
      }
    };

    fetchInitialNotifications();

    // Cleanup: 채널 및 타이머 확실히 해제
    return () => {
      isMounted = false;
      if (moowooExpiredTimerRef.current) {
        clearTimeout(moowooExpiredTimerRef.current);
        moowooExpiredTimerRef.current = null;
      }
      console.log('[관제탑] 구독 해제');
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // 3. conversation_id 기준 알림 Map 파생 (conversation_id가 null인 알림 제외)
  const notifsByConvId = useMemo(() => {
    const map = new Map();
    notifications.forEach((n) => {
      if (!n.conversation_id) return;
      if (!map.has(n.conversation_id)) {
        map.set(n.conversation_id, []);
      }
      map.get(n.conversation_id).push(n);
    });
    return map;
  }, [notifications]);

  // 4. 읽음 처리 함수
  const markAsRead = useCallback(
    async (notificationIdOrIds) => {
      if (!notificationIdOrIds || !userId) return;
      const ids = Array.isArray(notificationIdOrIds) ? notificationIdOrIds : [notificationIdOrIds];
      if (ids.length === 0) return;

      try {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('recipient_id', userId)
          .in('id', ids);

        if (error) {
          console.error('Error marking notification as read:', error);
        } else {
          setNotifications((prev) => prev.filter((n) => !ids.includes(n.id)));
        }
      } catch (err) {
        console.error('Error in markAsRead:', err);
      }
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      notifications,
      notifsByConvId,
      markAsRead,
      latestArrival,
      clearLatestArrival,
      notifSignal,
    }),
    [notifications, notifsByConvId, markAsRead, latestArrival, clearLatestArrival, notifSignal]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
